import type { DateTime } from "luxon";
import type { Day, NurseInShift, WeekData } from "../utils/models";

/**
 * Scripted "Ask Annie" answers for the hospital Shift Insights demo.
 * Matches a fixed set of questions by keyword; the clinical narrative is scripted
 * while counts/names/census/ratio/dates come from the viewed week's real data.
 * Anything that doesn't match falls through to the real backend.
 */

export enum AnnieShiftInsightsIntent {
	// Ordered by question priority: P1s, then P2s, then P3.
	TodayWorkload = "today_workload",
	RequestFloat = "request_float",
	MessageTonight = "message_tonight",
	HeaviestLoad = "heaviest_load",
	FloatsInOut = "floats_in_out",
	ChargeCertified = "charge_certified",
	WhosOnSchedule = "whos_on_schedule",
}

const includesAny = (msg: string, keywords: string[]): boolean => keywords.some((k) => msg.includes(k));

// Checks run in the questions' priority order (P1s, then P2s, then P3). Where two
// could match the same message, the earlier one carries an exclusion so the more
// specific question still wins (e.g. workload excludes "who ... heaviest").
export function detectAnnieShiftInsightsIntent(message: string): AnnieShiftInsightsIntent | null {
	const msg = message.toLowerCase();

	const asksHeaviest = msg.includes("who") && includesAny(msg, ["heaviest", "hardest", "busiest", "most"]);

	// P1 — today's workload. Excludes the heaviest-load phrasing, which also mentions "load".
	if (!asksHeaviest && includesAny(msg, ["workload", "load", "my shift"]) && includesAny(msg, ["today", "my", "current"])) {
		return AnnieShiftInsightsIntent.TodayWorkload;
	}
	// P1 — request a float from the supervisor.
	if (includesAny(msg, ["ask", "request", "message"]) && msg.includes("supervisor") && includesAny(msg, ["float", "additional", "extra"])) {
		return AnnieShiftInsightsIntent.RequestFloat;
	}
	// P1 — message tonight's shift about generator testing.
	if (
		includesAny(msg, ["message", "send", "tell", "notify"]) &&
		includesAny(msg, ["tonight", "night"]) &&
		includesAny(msg, ["generator", "testing", "everyone"])
	) {
		return AnnieShiftInsightsIntent.MessageTonight;
	}
	// P2 — heaviest load this shift.
	if (asksHeaviest) {
		return AnnieShiftInsightsIntent.HeaviestLoad;
	}
	// P2 — floats in or out today.
	if (includesAny(msg, ["float", "floating"]) && includesAny(msg, ["in", "out", "today"]) && !msg.includes("supervisor")) {
		return AnnieShiftInsightsIntent.FloatsInOut;
	}
	// P2 — is <name> charge-certified.
	if (msg.includes("charge") && includesAny(msg, ["certified", "certify", "certification"])) {
		return AnnieShiftInsightsIntent.ChargeCertified;
	}
	// P3 — who's on the schedule today.
	if (includesAny(msg, ["who", "schedule"]) && includesAny(msg, ["schedule", "working", "roster", "on today", "on the schedule"])) {
		return AnnieShiftInsightsIntent.WhosOnSchedule;
	}
	return null;
}

type DayWithDate = Day & { date: DateTime };

// Day rows have shift.type "Day"/"Morning"; everything else is the night row.
const isDayShiftType = (type: string): boolean => type === "Day" || type === "Morning";

// The Day-shift and Night-shift cells for a given date (either may be missing).
function findShiftsForDate(week: WeekData, date: DateTime): { day: DayWithDate | null; night: DayWithDate | null } {
	const target = date.startOf("day");
	let day: DayWithDate | null = null;
	let night: DayWithDate | null = null;
	for (const shift of week.weeklyShifts) {
		const match = shift.days.find((d) => d?.date?.startOf("day").equals(target));
		if (!match) continue;
		if (isDayShiftType(shift.type)) day = match as DayWithDate;
		else night = match as DayWithDate;
	}
	return { day, night };
}

// "25–27" style census band.
function censusBand(day: Day): string {
	if (day.census_low === day.census_high) return `${day.census_low}`;
	return `${day.census_low}–${day.census_high}`;
}

// Midpoint of the census band, for ratio math.
function censusMid(day: Day): number {
	return Math.round((day.census_low + day.census_high) / 2);
}

const nurseNames = (day: Day | null): string[] => (day?.nurses ?? []).map((n) => n.name).filter(Boolean);

const chargeNurse = (day: Day | null): NurseInShift | undefined => (day?.nurses ?? []).find((n) => n.charge);

// "A, B and C" list.
function joinList(items: string[]): string {
	if (items.length <= 1) return items.join("");
	return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

const NO_TODAY_FALLBACK = "I can't see today on the current view — flip to the week that includes today and ask me again.";

function answerTodayWorkload(day: DayWithDate | null): string {
	if (!day) return NO_TODAY_FALLBACK;
	const nurses = day.nurses?.length ?? 0;
	const census = censusMid(day);
	const ratio = nurses > 0 ? Math.round(census / nurses) : 0;
	const weekday = day.date.toFormat("cccc");
	return (
		`Your morning shift is running **slightly below a normal ${weekday}.** ` +
		`Census is ${censusBand(day)} against ${nurses} nurses, which holds you at roughly a **1:${ratio}** ratio — within target and on the lighter end. ` +
		"The post-op load is light too: **1 surgical admission expected** (total knee) and 1 discharge (total hip going home)."
	);
}

function answerRequestFloat(tomorrowDay: DayWithDate | null, tomorrow: DateTime): string {
	const nurses = tomorrowDay?.nurses?.length ?? 7;
	const census = tomorrowDay ? censusMid(tomorrowDay) : 39;
	const date = tomorrow.toFormat("LLL d");
	return (
		"Yes, that makes sense — we're understaffed for tomorrow's morning shift.\n\n" +
		`I'll send a message to the house supervisor: "Requesting one additional float for the unit, morning shift ${date}. ` +
		`Scheduled ${nurses} RN / projected census ${census} with 5 post-op admissions — requesting coverage to stay within ratio during the admission wave."\n\n` +
		"I'll follow up once we get an answer."
	);
}

function answerHeaviestLoad(day: DayWithDate | null): string {
	if (!day) return NO_TODAY_FALLBACK;
	// Heaviest = a real non-charge nurse on the day shift; the acuity narrative is scripted.
	const nonCharge = (day.nurses ?? []).filter((n) => !n.charge);
	const heaviest = nonCharge[0]?.name ?? day.nurses?.[0]?.name ?? "the assigned nurse";
	const lighter = nonCharge.slice(1, 3).map((n) => n.name);
	const rebalanceTo = lighter.length ? joinList(lighter) : "another nurse at a standard load";
	return (
		`**${heaviest}** has the heaviest load this shift. They have 6 patients — the same count as several others — but the mix is the reason. ` +
		"They're carrying a post-op Day 1 spinal fusion that needs frequent neuro checks and active pain management, and on top of that 3 pending post-op admissions: " +
		"a total knee replacement, a lap cholecystectomy, and an appendectomy. Each fresh post-op is heavy on assessment and documentation. " +
		"That combination puts them well above the others, who are carrying at most one admission or a discharge. " +
		`If you want to rebalance, the cleanest option is shifting one of ${heaviest}'s incoming admissions to ${rebalanceTo}, at a standard 6 with no admissions. ` +
		"Want me to draft that reassignment?"
	);
}

function answerMessageTonight(night: DayWithDate | null): string {
	const names = nurseNames(night);
	const charge = chargeNurse(night)?.name ?? names[0] ?? "the charge nurse";
	const recipients = names.length ? joinList(names) : "tonight's nurses";
	return (
		`I'll send to the ${names.length || 3} nurses on tonight's shift: ${recipients}.\n\n` +
		'"Heads up for tonight: Facilities has scheduled generator testing on the unit between 2:00–4:00 AM. ' +
		"You may see brief hallway light flickers; patient rooms, monitors, and pumps run on backup and won't be affected. " +
		`Charge is ${charge} — reply here with any questions."`
	);
}

function answerFloatsInOut(day: DayWithDate | null, night: DayWithDate | null): string {
	const total = (day?.nurses?.length ?? 0) + (night?.nurses?.length ?? 0);
	return (
		`No floats in or out of the unit today. All ${total || 10} nurses are your home-unit staff, and the float pool hasn't assigned anyone to you. ` +
		"Given morning is only slightly above baseline and night is light, you're covered without a float."
	);
}

function answerChargeCertified(message: string): string {
	// Echo whatever name was typed; the answer is always "not certified" (scripted).
	const name = extractName(message) ?? "That nurse";
	return (
		`No — **${name}** is not charge-certified. ` +
		"They've completed their med/surg competencies but haven't finished the charge-nurse orientation, " +
		"so they can't take charge or count as your charge coverage."
	);
}

// Best-effort name pull from "is <Name> certified for charge?" — the words after
// "is" up to "certified"/"charge". Falls back to null so the caller uses a generic.
function extractName(message: string): string | null {
	const match = message.match(/is\s+(.+?)\s+(?:certified|charge|cert)/i);
	if (!match) return null;
	const name = match[1].trim();
	return name.length ? name.replace(/\b\w/g, (c) => c.toUpperCase()) : null;
}

function answerWhosOnSchedule(day: DayWithDate | null, night: DayWithDate | null): string {
	const dayCharge = chargeNurse(day)?.name;
	const nightCharge = chargeNurse(night)?.name;
	const dayNames = nurseNames(day);
	const nightNames = nurseNames(night);

	const withCharge = (names: string[], charge?: string) => names.map((n) => (n === charge ? `${n} (charge)` : n)).join(", ") || "no nurses scheduled";

	const total = dayNames.length + nightNames.length;
	return (
		`You have ${total} nurses across both shifts on the unit today.\n\n` +
		`**Day (${dayNames.length}):** ${withCharge(dayNames, dayCharge)}.\n\n` +
		`**Night (${nightNames.length}):** ${withCharge(nightNames, nightCharge)}.\n\n` +
		"Day is staffed heavier because census and post-op admission volume peak during the day; nights run lighter on a standard weekday."
	);
}

// `now` is passed in (not read from the clock) so the caller controls "today".
export function buildAnnieShiftInsightsAnswer(intent: AnnieShiftInsightsIntent, week: WeekData | undefined, now: DateTime, message: string): string {
	if (!week) return NO_TODAY_FALLBACK;
	const { day, night } = findShiftsForDate(week, now);

	switch (intent) {
		case AnnieShiftInsightsIntent.TodayWorkload:
			return answerTodayWorkload(day);
		case AnnieShiftInsightsIntent.RequestFloat: {
			const tomorrow = now.plus({ days: 1 });
			return answerRequestFloat(findShiftsForDate(week, tomorrow).day, tomorrow);
		}
		case AnnieShiftInsightsIntent.HeaviestLoad:
			return answerHeaviestLoad(day);
		case AnnieShiftInsightsIntent.MessageTonight:
			return answerMessageTonight(night);
		case AnnieShiftInsightsIntent.FloatsInOut:
			return answerFloatsInOut(day, night);
		case AnnieShiftInsightsIntent.ChargeCertified:
			return answerChargeCertified(message);
		case AnnieShiftInsightsIntent.WhosOnSchedule:
			return answerWhosOnSchedule(day, night);
	}
}
